/* eslint-disable no-unused-expressions */

import { API_URL } from '../api';
import * as Sentry from '@sentry/browser';
import sentryUtils from './sentry';
import { ZendeskLoginFailedError } from 'types/3rd-party';

// Delay needed to prevent widget redraw and other odd behaviour
// while Zendesk is authenticating or logging out the user
const SHOW_WIDGET_DELAY = 1500;

export enum ZENDESK_FIELDS {
  ENTERPRISE = '33146272220435',
  ENVIRONMENT = '33241196270995',
}

const WIDGET_AUTHENTICATED = 'zendesk:authenticated';

const setLocale = (locale: string) => {
  window.zE && window.zE('messenger:set', 'locale', locale);
};

// TODO: This is the new way based on the documentation to add the tags but is not functional right now (To be completed when needed)
const addTag = (tag?: string) => {
  window.zE && window.zE('messenger:set', 'conversationTags', [tag]);
};

const updateConversationField = (fieldId: string, data: string) => {
  data && window.zE('messenger:set', 'conversationFields', [{ id: fieldId, value: data }]);
};

const init = () => {
  // Add environment conversation field based on environment variable or default to chat session to be used in custom views in Zendesk Support workspaces
  const environment =
    typeof import.meta.env.REACT_APP_ZENDESK_WIDGET_ENV_TAG !== 'undefined' ? import.meta.env.REACT_APP_ZENDESK_WIDGET_ENV_TAG : 'development';
  updateConversationField(ZENDESK_FIELDS.ENVIRONMENT, environment);
};

const hideWidget = () => {
  window.zE && window.zE('messenger', 'hide');
};

const openWidget = () => {
  window.zE && window.zE('messenger', 'open');
};

const showWidget = () => {
  window.zE && window.zE('messenger', 'show');
};

/**
 * Authenticate the Zendesk Messenger with the service
 * @param locale The locale to set for the Zendesk Messenger
 */
const authenticateZendeskMessenger = (locale: string) => {
  setLocale(locale);
  window.zE(
    'messenger',
    'loginUser',
    (callback: (jwt: string) => void) => {
      fetch(`${API_URL}/zendesk/token`, { credentials: 'include' })
        .then(res => {
          if (!res.ok) {
            throw Error('Could not authenticate with Zendesk. Messenger initialized anonymously');
          }
          return res.text();
        })
        .then(jwt => {
          callback(jwt); // Pass the JWT token to the callback to authenticate the user
          window.dispatchEvent(new Event(WIDGET_AUTHENTICATED));
        })
        .catch(error => {
          console.warn('Error authenticating Zendesk Messenger:', error);
        });
    },
    (error: ZendeskLoginFailedError) => {
      if (!error) {
        return;
      }
      console.warn('Error authenticating Zendesk Messenger:', error.message);
      window.zE('messenger', 'logoutUser');
      const severity = sentryUtils.isUserConflictError(error) ? Sentry.Severity.Error : Sentry.Severity.Log;
      const message = sentryUtils.isUserConflictError(error) ? '' : 'Messenger initialized anonymously';
      sentryUtils.captureError(new Error(`Error Zendesk Messenger: ${error.reason} - ${error.message} - ${message}`), {}, severity);
    },
  );
};

const logout = () => {
  hideWidget();
  window.zE && window.zE('messenger', 'logoutUser');
  setTimeout(() => showWidget(), SHOW_WIDGET_DELAY);
};

export default {
  authenticateZendeskMessenger,
  hideWidget,
  init,
  logout,
  openWidget,
  setLocale,
  addTag,
  updateConversationField,
  showWidget,
  SHOW_WIDGET_DELAY,
  WIDGET_AUTHENTICATED,
};
